import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  DollarSign,
  Gauge,
  Users,
  MapPin,
  FileDown,
  Calendar,
} from 'lucide-react';

interface ClientCost {
  client_name: string;
  total_cost: number;
  fuel: number;
  maintenance: number;
  depreciation: number;
  insurance: number;
  client_miles: number;
  visit_count: number;
}

interface CostSummary {
  period: string;
  total_cost: number;
  total_miles: number;
  client_count: number;
  total_visits: number;
  by_client: ClientCost[];
}

const MOCK_SUMMARY: CostSummary = {
  period: '2026-03',
  total_cost: 14230.5,
  total_miles: 8940,
  client_count: 12,
  total_visits: 156,
  by_client: [],
};

const COST_COLORS = {
  fuel: '#3B82F6',
  maintenance: '#EAB308',
  depreciation: '#9CA3AF',
  insurance: '#A855F7',
};

export default function CostAllocationPage() {
  const [period, setPeriod] = useState('2026-03');
  const { data, isLoading } = useApi<CostSummary>(`/api/cost-allocation/summary?period=${period}`);
  const d = data ?? MOCK_SUMMARY;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cost Allocation"
        description="Per-client cost breakdown and analysis"
        actions={
          <div className="flex items-center gap-3">
            {isLoading && <Spinner size={16} />}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
              <input
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-sm"
                style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
              />
            </div>
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ background: '#10B981', color: '#fff' }}
            >
              <FileDown className="w-4 h-4" />
              Generate Report
            </button>
          </div>
        }
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Total Cost" value={`$${d.total_cost.toLocaleString()}`} accent="success" />
        <StatCard icon={Gauge} label="Total Miles" value={d.total_miles.toLocaleString()} />
        <StatCard icon={Users} label="Client Count" value={d.client_count} />
        <StatCard icon={MapPin} label="Total Visits" value={d.total_visits} />
      </div>

      {/* Cost Legend */}
      <div className="flex items-center gap-4 flex-wrap">
        {Object.entries(COST_COLORS).map(([key, color]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded" style={{ background: color }} />
            <span className="text-xs capitalize" style={{ color: 'var(--muted-foreground)' }}>
              {key}
            </span>
          </div>
        ))}
      </div>

      {/* Client Cost Breakdown */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
            <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
              Client Cost Breakdown
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  {['Client', 'Total Cost', 'Breakdown', 'Miles', 'Visits'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.by_client.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      No cost data for this period
                    </td>
                  </tr>
                ) : (
                  d.by_client.map((c) => {
                    const total = c.fuel + c.maintenance + c.depreciation + c.insurance;
                    const pct = (v: number) => (total > 0 ? (v / total) * 100 : 0);
                    return (
                      <tr key={c.client_name} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        <td className="px-4 py-3 font-medium" style={{ color: 'var(--foreground)' }}>{c.client_name}</td>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--foreground)' }}>
                          ${c.total_cost.toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex h-2.5 rounded-full overflow-hidden w-40">
                              <div style={{ width: `${pct(c.fuel)}%`, background: COST_COLORS.fuel }} />
                              <div style={{ width: `${pct(c.maintenance)}%`, background: COST_COLORS.maintenance }} />
                              <div style={{ width: `${pct(c.depreciation)}%`, background: COST_COLORS.depreciation }} />
                              <div style={{ width: `${pct(c.insurance)}%`, background: COST_COLORS.insurance }} />
                            </div>
                            <div className="flex gap-1.5 text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                              <span>${c.fuel}</span>
                              <span>/</span>
                              <span>${c.maintenance}</span>
                              <span>/</span>
                              <span>${c.depreciation}</span>
                              <span>/</span>
                              <span>${c.insurance}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{c.client_miles.toLocaleString()}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{c.visit_count}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
